import React, { useCallback, useEffect, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaWhatsapp } from 'react-icons/fa';
import { orderTrackingService } from '../../services/apiService';
import { toast } from 'react-toastify';

/**
 * Delivery OTP — code sent via SMS + WhatsApp; buyer enters to confirm receipt.
 */
export default function DeliveryOTP({ orderId, mockState, onConfirmed }) {
  const [meta, setMeta] = useState(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await orderTrackingService.getDeliveryOtp(orderId, mockState);
      setMeta(data);
    } catch (err) {
      setError(err.message || 'Could not load delivery status');
    } finally {
      setLoading(false);
    }
  }, [orderId, mockState]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!otp || String(otp).trim().length !== 4) {
      toast.error('Enter the 4-digit code');
      return;
    }
    setSubmitting(true);
    try {
      const res = await orderTrackingService.verifyDeliveryOtp(orderId, String(otp).trim());
      setConfirmed(true);
      toast.success('Delivery confirmed — 48-hour claim window open');
      onConfirmed?.(res?.order || res);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Invalid OTP');
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <section className="rounded-2xl border-2 border-emerald-500/50 bg-emerald-950/30 p-5">
        <div className="flex items-center gap-2 text-emerald-300 font-bold">
          <FaCheckCircle />
          Delivery confirmed
        </div>
        <p className="text-sm text-emerald-100/80 mt-2">
          You can raise a claim within 48 hours if there is an issue with your order.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border-2 border-amber-500/60 bg-gradient-to-b from-amber-950/40 to-slate-900/80 p-5 sm:p-6"
      aria-labelledby="delivery-otp-heading"
    >
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-600/40 bg-amber-950/50 px-4 py-3">
        <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-400" aria-hidden />
        <div>
          <p className="text-sm font-bold text-amber-200">
            Confirm receipt with your delivery code
          </p>
          <p className="mt-1 text-xs text-amber-100/80">
            A 4-digit code was sent via SMS and WhatsApp when the carrier attempted delivery.
            Enter it only after you have received and checked your package.
          </p>
        </div>
      </div>

      <h2
        id="delivery-otp-heading"
        className="text-center text-xs font-bold uppercase tracking-widest text-slate-400"
      >
        Enter delivery OTP
      </h2>

      {loading ? (
        <p className="mt-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
          <FaSpinner className="animate-spin" /> Loading…
        </p>
      ) : error ? (
        <p className="mt-6 text-center text-sm text-red-300">{error}</p>
      ) : (
        <form onSubmit={submitOtp} className="mt-5 space-y-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-full text-center font-mono text-4xl tracking-[0.5em] rounded-xl border-2 border-amber-500/40 bg-slate-900 py-4 text-amber-200 focus:border-amber-400 focus:outline-none"
            placeholder="••••"
            autoComplete="one-time-code"
          />
          {meta?.expiresAt && (
            <p className="text-center text-xs text-slate-500">
              Code valid until{' '}
              {new Date(meta.expiresAt).toLocaleString('en-IN', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || otp.length !== 4}
            className="w-full min-h-[52px] rounded-xl bg-emerald-600 font-bold text-white hover:bg-emerald-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
            Confirm delivery
          </button>
        </form>
      )}

      {(meta?.sentToWhatsApp || meta?.sentToSms) && (
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs text-slate-400">
          {meta.sentToSms && <span>Sent via SMS</span>}
          {meta.sentToWhatsApp && (
            <span className="inline-flex items-center gap-1 text-emerald-400">
              <FaWhatsapp /> Sent via WhatsApp
            </span>
          )}
        </div>
      )}
    </section>
  );
}
